import { useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  useCreateTechPayRate,
  useUpdateTechPayRate,
} from '@/hooks/use-payroll';
import { useAllPersons } from '@/hooks/use-persons';
import { useProjects } from '@/hooks/use-projects';
import type { CreateTechPayRatePayload, TechPayRate } from '@/lib/types';

type PayRateFormProps = {
  fixedProject?: { id: number; name: string };
  rate?: TechPayRate;
  onDone(): void;
};

type AppliesTo = 'worker' | 'job';

export function PayRateForm({ fixedProject, rate, onDone }: PayRateFormProps) {
  const { data: persons = [] } = useAllPersons();
  const { data: projectsData } = useProjects({ page_size: 1000 });
  const createRate = useCreateTechPayRate();
  const updateRate = useUpdateTechPayRate();
  const [appliesTo, setAppliesTo] = useState<AppliesTo>(
    rate?.applies_to === 'job' ? 'job' : 'worker',
  );
  const [workerId, setWorkerId] = useState(rate?.person ? String(rate.person) : '');
  const [projectId, setProjectId] = useState(
    String(fixedProject?.id ?? rate?.project ?? ''),
  );
  const [effectiveDate, setEffectiveDate] = useState(rate?.effective_date ?? '');
  const [hourlyRate, setHourlyRate] = useState(rate?.hourly_rate ?? '');
  const [overtimeRate, setOvertimeRate] = useState(rate?.overtime_rate ?? '');
  const [dailyRate, setDailyRate] = useState(rate?.daily_rate ?? '');
  const [perDiemRate, setPerDiemRate] = useState(rate?.per_diem_rate ?? '');
  const [notes, setNotes] = useState(rate?.notes ?? '');
  const [errors, setErrors] = useState<string[]>([]);

  const pending = createRate.isPending || updateRate.isPending;
  const projects = projectsData?.results ?? [];

  const handleSubmit = async () => {
    const nextErrors: string[] = [];
    if (!rate && appliesTo === 'worker' && !workerId) {
      nextErrors.push('Choose a worker.');
    }
    if (!rate && appliesTo === 'job' && !fixedProject && !projectId) {
      nextErrors.push('Choose a job for a job rate.');
    }
    if (!effectiveDate) {
      nextErrors.push('Enter an effective date.');
    }
    setErrors(nextErrors);
    if (nextErrors.length) return;

    const common = {
      effective_date: effectiveDate,
      hourly_rate: hourlyRate || null,
      overtime_rate: overtimeRate || null,
      daily_rate: dailyRate || null,
      per_diem_rate: perDiemRate || null,
      notes,
    };
    try {
      if (rate) {
        await updateRate.mutateAsync({ id: rate.id, data: common });
      } else {
        const payload: CreateTechPayRatePayload = appliesTo === 'job'
          ? {
              ...common,
              person: null,
              project: fixedProject?.id ?? Number(projectId),
            }
          : {
              ...common,
              person: Number(workerId),
              project: fixedProject?.id ?? (projectId ? Number(projectId) : null),
            };
        await createRate.mutateAsync(payload);
      }
      onDone();
    } catch {
      setErrors(['Failed to save pay rate.']);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{rate ? 'Edit Pay Rate' : 'New Pay Rate'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {errors.map((message) => (
          <p key={message} role="alert" className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">
            {message}
          </p>
        ))}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="pay-rate-applies-to">Applies to</Label>
            {rate ? (
              <p className="mt-2 text-sm text-gray-700">
                {rate.applies_to === 'job' ? 'Everyone on this job' : rate.project != null ? 'This worker on this job' : 'This worker'}
              </p>
            ) : (
              <Select
                value={appliesTo}
                onValueChange={(value: AppliesTo) => {
                  setAppliesTo(value);
                  if (value === 'job') setWorkerId('');
                }}
              >
                <SelectTrigger id="pay-rate-applies-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="worker">This worker</SelectItem>
                  <SelectItem value="job">Everyone on this job</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {(rate || appliesTo === 'worker') && (
            <div>
              <Label htmlFor="pay-rate-worker">Worker</Label>
              {rate ? (
                <p className="mt-2 text-sm text-gray-700">{rate.person_name ?? rate.tech_name ?? 'Unknown'}</p>
              ) : (
                <Select value={workerId} onValueChange={setWorkerId}>
                  <SelectTrigger id="pay-rate-worker">
                    <SelectValue placeholder="Select worker" />
                  </SelectTrigger>
                  <SelectContent>
                    {persons.map((person) => (
                      <SelectItem key={person.id} value={String(person.id)}>
                        {person.first_name} {person.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="pay-rate-job">Job</Label>
            {fixedProject ? (
              <p className="mt-2 text-sm text-gray-700">{fixedProject.name}</p>
            ) : rate ? (
              <p className="mt-2 text-sm text-gray-700">{rate.project_name ?? 'Default'}</p>
            ) : (
              <Select value={projectId || 'none'} onValueChange={(value) => setProjectId(value === 'none' ? '' : value)}>
                <SelectTrigger id="pay-rate-job">
                  <SelectValue placeholder="No job (default rate)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No job (default rate)</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <Label htmlFor="pay-rate-effective-date">Effective date</Label>
            <Input
              id="pay-rate-effective-date"
              type="date"
              value={effectiveDate}
              onChange={(event) => setEffectiveDate(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="pay-rate-hourly">Hourly</Label>
            <Input id="pay-rate-hourly" type="number" step="0.01" placeholder="0.00" value={hourlyRate} onChange={(event) => setHourlyRate(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="pay-rate-overtime">Overtime</Label>
            <Input id="pay-rate-overtime" type="number" step="0.01" placeholder="Auto if blank" value={overtimeRate} onChange={(event) => setOvertimeRate(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="pay-rate-daily">Daily</Label>
            <Input id="pay-rate-daily" type="number" step="0.01" placeholder="0.00" value={dailyRate} onChange={(event) => setDailyRate(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="pay-rate-per-diem">Per diem</Label>
            <Input id="pay-rate-per-diem" type="number" step="0.01" placeholder="0.00" value={perDiemRate} onChange={(event) => setPerDiemRate(event.target.value)} />
          </div>
        </div>

        <div>
          <Label htmlFor="pay-rate-notes">Notes</Label>
          <Input id="pay-rate-notes" placeholder="Optional notes..." value={notes} onChange={(event) => setNotes(event.target.value)} />
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSubmit} disabled={pending}>
            {pending ? 'Saving...' : 'Save Rate'}
          </Button>
          <Button variant="outline" onClick={onDone}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}
